import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/redux-store';
import { useGetPlatformsQuery } from 'src/redux/admin/reporting/reporting.endpoints';
import { MartTimeSeriesPoint } from 'src/redux/admin/mart-reporting/mart-reporting.types';
import { formatDisplayDate } from 'src/helpers/date.helpers';
import {
  BetaMetricCard,
  BetaMetricCardRow,
  BetaSectionHeading,
  BetaTimeSeriesChart,
} from 'src/pages/reports/components/beta-charts';
import {
  useGetOverviewAggregateDataQuery,
  useGetOverviewTimeSeriesDataQuery,
} from '../endpoints/overview.endpoints';
import { AggregateSourceData, PlatformData } from '../types/performance.types';
import { getPlatformMargin } from '../utils/platform-margin';
import {
  getInitialOverviewDates,
  OverviewDateFilterType,
  resolveOverviewDateRange,
} from '../utils/overview-date-range';
import { OverviewBetaControls } from '../components/overview-beta/overview-beta-controls';
import { OverviewBetaPlatformStrip } from '../components/overview-beta/overview-beta-platform-strip';
import { OverviewBetaFunnel } from '../components/overview-beta/overview-beta-funnel';
import { OverviewBetaPlatformShare } from '../components/overview-beta/overview-beta-platform-share';
import { OverviewBetaCumulativeSpend } from '../components/overview-beta/overview-beta-cumulative-spend';
import { OverviewBetaImpressionsSpend } from '../components/overview-beta/overview-beta-impressions-spend';
import {
  buildEfficiencyMetrics,
  OverviewBetaEfficiency,
} from '../components/overview-beta/overview-beta-efficiency';
import { OverviewBetaPlatformComparison } from '../components/overview-beta/overview-beta-platform-comparison';
import { OverviewBetaSpendSegments } from '../components/overview-beta/overview-beta-spend-segments';
import { OverviewBetaConversionsTrend } from '../components/overview-beta/overview-beta-conversions-trend';
import {
  deriveCpa,
  deriveCpc,
  deriveCpm,
  deriveCtr,
  formatCompactNumber,
  formatPercentChangeLabel,
  formatPlatformDisplayName,
  getChangeDirection,
  PLATFORM_CHART_COLOURS,
  safePercentChange,
} from '../components/overview-beta/overview-beta-format';

const OverviewBetaTab: React.FC = () => {
  const activeClient = useSelector((state: RootState) => state.userState.activeClient);
  const [dateFilter, setDateFilter] = useState<OverviewDateFilterType>('this_month');
  const [editMode, setEditMode] = useState(false);
  const [startDate, setStartDate] = useState(getInitialOverviewDates().start);
  const [endDate, setEndDate] = useState(getInitialOverviewDates().end);
  const [activePlatforms, setActivePlatforms] = useState<Record<string, PlatformData>>({});

  const {
    data: platforms,
    isLoading: isLoadingPlatforms,
  } = useGetPlatformsQuery({ previewClientId: activeClient?.ext_client_id });

  const {
    data: timeSeriesData,
    isLoading: isLoadingTimeSeries,
  } = useGetOverviewTimeSeriesDataQuery({
    start_date: startDate,
    end_date: endDate,
    extClientId: activeClient?.ext_client_id,
  });

  const {
    data: aggregateData,
    isLoading: isLoadingAggregate,
  } = useGetOverviewAggregateDataQuery({
    start_date: startDate,
    end_date: endDate,
    filter: dateFilter,
    extClientId: activeClient?.ext_client_id,
  });

  const isLoading = isLoadingPlatforms || isLoadingAggregate || isLoadingTimeSeries;

  useEffect(() => {
    if (!platforms || !aggregateData || !timeSeriesData) return;

    setActivePlatforms((prev) => {
      const next: Record<string, PlatformData> = {};
      platforms.forEach((platform: string, index: number) => {
        next[platform] = {
          name: platform,
          displayName: formatPlatformDisplayName(platform),
          past: aggregateData?.sources[platform]?.past as unknown as Record<string, AggregateSourceData>,
          dif: aggregateData?.sources[platform]?.dif as unknown as Record<string, AggregateSourceData>,
          aggregateData: aggregateData?.sources[platform]?.current as unknown as Record<string, AggregateSourceData>,
          timeSeriesData: timeSeriesData?.sources[platform] as unknown as Record<string, AggregateSourceData[]>,
          color: '',
          chartColor: PLATFORM_CHART_COLOURS[index % PLATFORM_CHART_COLOURS.length],
          enabled: prev[platform]?.enabled ?? true,
          margin: getPlatformMargin(platform),
        };
      });
      return next;
    });
  }, [platforms, aggregateData, timeSeriesData]);

  const enabledPlatforms = useMemo(
    () => Object.values(activePlatforms).filter((platform) => platform.enabled),
    [activePlatforms]
  );

  const totals = useMemo(() => {
    const sumMetric = (
      picker: (platform: PlatformData) => number
    ) => enabledPlatforms.reduce((acc, platform) => acc + picker(platform), 0);

    const currentSpend = sumMetric(
      (platform) => Number(platform.aggregateData?.spend ?? 0) * (1 + platform.margin / 100)
    );
    const pastSpend = sumMetric(
      (platform) => Number(platform.past?.spend ?? 0) * (1 + platform.margin / 100)
    );
    const impressions = sumMetric((platform) => Number(platform.aggregateData?.impressions ?? 0));
    const pastImpressions = sumMetric((platform) => Number(platform.past?.impressions ?? 0));
    const clicks = sumMetric((platform) => Number(platform.aggregateData?.clicks ?? 0));
    const pastClicks = sumMetric((platform) => Number(platform.past?.clicks ?? 0));
    const conversions = sumMetric((platform) => Number(platform.aggregateData?.conversions ?? 0));
    const pastConversions = sumMetric((platform) => Number(platform.past?.conversions ?? 0));

    const ctr = deriveCtr(clicks, impressions);
    const pastCtr = deriveCtr(pastClicks, pastImpressions);
    const cpc = deriveCpc(currentSpend, clicks);
    const pastCpc = deriveCpc(pastSpend, pastClicks);
    const cpm = deriveCpm(currentSpend, impressions);
    const pastCpm = deriveCpm(pastSpend, pastImpressions);
    const cpa = deriveCpa(currentSpend, conversions);
    const pastCpa = deriveCpa(pastSpend, pastConversions);

    return {
      spend: currentSpend,
      pastSpend,
      impressions,
      pastImpressions,
      clicks,
      pastClicks,
      conversions,
      pastConversions,
      ctr,
      pastCtr,
      cpc,
      pastCpc,
      cpm,
      pastCpm,
      cpa,
      pastCpa,
      spendChange: safePercentChange(currentSpend, pastSpend),
      impressionsChange: safePercentChange(impressions, pastImpressions),
      clicksChange: safePercentChange(clicks, pastClicks),
      conversionsChange: safePercentChange(conversions, pastConversions),
      ctrChange: safePercentChange(ctr, pastCtr),
      cpcChange: safePercentChange(cpc, pastCpc),
      cpmChange: safePercentChange(cpm, pastCpm),
      cpaChange: safePercentChange(cpa, pastCpa),
    };
  }, [enabledPlatforms]);

  const mergedTimeSeries = useMemo((): MartTimeSeriesPoint[] => {
    const dateMap = new Map<string, MartTimeSeriesPoint>();

    enabledPlatforms.forEach((platform) => {
      const series = timeSeriesData?.sources?.[platform.name];
      if (!Array.isArray(series)) return;

      series.forEach((point) => {
        if (!point?.date) return;
        const existing = dateMap.get(point.date) ?? {
          date: point.date,
          impressions: 0,
          clicks: 0,
          spend: 0,
          conversions: 0,
          video_starts: 0,
          video_completions: 0,
          video_completes: 0,
          views: 0,
        };

        const spend = Number(point.spend ?? 0) * (1 + platform.margin / 100);
        existing.impressions += Number(point.impressions ?? 0);
        existing.clicks += Number(point.clicks ?? 0);
        existing.spend += spend;
        existing.conversions += Number(point.conversions ?? 0);
        existing.views += Number(point.views ?? 0);
        existing.video_starts += Number(point.video_starts ?? 0);
        existing.video_completes += Number(point.video_completes ?? 0);
        existing.video_completions += Number(point.video_completes ?? 0);
        dateMap.set(point.date, existing);
      });
    });

    return Array.from(dateMap.values()).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [enabledPlatforms, timeSeriesData]);

  const sparklineByMetric = useMemo(() => {
    const build = (metric: keyof MartTimeSeriesPoint) =>
      mergedTimeSeries.map((point) => ({ value: Number(point[metric] ?? 0) }));
    return {
      spend: build('spend'),
      impressions: build('impressions'),
      clicks: build('clicks'),
      conversions: build('conversions'),
    };
  }, [mergedTimeSeries]);

  const dateLabelPoints = useMemo(
    () =>
      mergedTimeSeries.map((point) => ({
        ...point,
        dateLabel: formatDisplayDate(point.date),
      })),
    [mergedTimeSeries]
  );

  const cumulativeSpend = useMemo(() => {
    let running = 0;
    return dateLabelPoints.map((point) => {
      running += Number(point.spend ?? 0);
      return {
        date: point.date,
        dateLabel: point.dateLabel,
        daily: Number(point.spend ?? 0),
        cumulative: running,
      };
    });
  }, [dateLabelPoints]);

  const impressionsSpend = useMemo(
    () =>
      dateLabelPoints.map((point) => ({
        date: point.date,
        dateLabel: point.dateLabel,
        impressions: Number(point.impressions ?? 0),
        spend: Number(point.spend ?? 0),
      })),
    [dateLabelPoints]
  );

  const platformShare = useMemo(
    () =>
      enabledPlatforms.map((platform) => ({
        name: platform.name,
        value: Number(platform.aggregateData?.spend ?? 0) * (1 + platform.margin / 100),
        colour: platform.chartColor,
      })),
    [enabledPlatforms]
  );

  const platformComparison = useMemo(
    () =>
      enabledPlatforms.map((platform) => {
        const spend = Number(platform.aggregateData?.spend ?? 0) * (1 + platform.margin / 100);
        const impressions = Number(platform.aggregateData?.impressions ?? 0);
        const clicks = Number(platform.aggregateData?.clicks ?? 0);
        const conversions = Number(platform.aggregateData?.conversions ?? 0);
        return {
          name: platform.name,
          displayName: platform.displayName,
          colour: platform.chartColor,
          spend,
          impressions,
          clicks,
          conversions,
          ctr: deriveCtr(clicks, impressions),
        };
      }),
    [enabledPlatforms]
  );

  const funnelSteps = useMemo(
    () => [
      { label: 'Impressions', value: totals.impressions, colour: '#059669' },
      { label: 'Clicks', value: totals.clicks, colour: '#F59E0B' },
      { label: 'Conversions', value: totals.conversions, colour: '#2563EB' },
    ],
    [totals]
  );

  const efficiencyMetrics = useMemo(
    () =>
      buildEfficiencyMetrics({
        ctr: totals.ctr,
        pastCtr: totals.pastCtr,
        cpc: totals.cpc,
        pastCpc: totals.pastCpc,
        cpm: totals.cpm,
        pastCpm: totals.pastCpm,
        cpa: totals.cpa,
        pastCpa: totals.pastCpa,
        ctrChange: totals.ctrChange,
        cpcChange: totals.cpcChange,
        cpmChange: totals.cpmChange,
        cpaChange: totals.cpaChange,
      }),
    [totals]
  );

  const subtitle = `${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`;

  const handleDateChange = (nextFilter: string, customStart?: string, customEnd?: string) => {
    const filter = nextFilter as OverviewDateFilterType;
    setDateFilter(filter);
    const range = resolveOverviewDateRange(filter, customStart, customEnd);
    setStartDate(range.start);
    setEndDate(range.end);
  };

  const handlePlatformChange = (platform: PlatformData) => {
    setActivePlatforms((prev) => ({
      ...prev,
      [platform.name]: { ...platform },
    }));
  };

  return (
    <div className="h-full overflow-y-auto bg-[#F4F5F8] px-6 py-6 sm:px-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold leading-tight tracking-[-0.6px] text-[#0D0F1A]">
            Overview (Beta)
          </h1>
          <p className="mt-1 text-[13px] text-[#5E6278]">
            Cross-platform performance dashboard ·
            {' '}
            {subtitle}
          </p>
        </div>
        <span className="mt-0.5 box-border inline-flex h-6 shrink-0 items-center justify-center rounded-full border border-[#D9DBE3] bg-white px-3 text-[11px] font-bold uppercase leading-none tracking-[0.06em] text-[#5E6278]">
          Beta
        </span>
      </div>

      <OverviewBetaControls
        dateFilter={dateFilter}
        editMode={editMode}
        onDateChange={handleDateChange}
        onEditToggle={() => setEditMode((value) => !value)}
      />

      <OverviewBetaPlatformStrip
        platforms={activePlatforms}
        editMode={editMode}
        onPlatformChange={handlePlatformChange}
      />

      <BetaSectionHeading>
        01 — KPI Overview
      </BetaSectionHeading>
      <BetaMetricCardRow>
        <BetaMetricCard
          label="Spend"
          variant="spend"
          isLoading={isLoading}
          value={formatCompactNumber(totals.spend, { currency: true })}
          changeLabel={formatPercentChangeLabel(totals.spendChange)}
          changeDirection={getChangeDirection(totals.spendChange)}
          sparklineData={sparklineByMetric.spend}
        />
        <BetaMetricCard
          label="Impressions"
          variant="impressions"
          isLoading={isLoading}
          value={formatCompactNumber(totals.impressions)}
          changeLabel={formatPercentChangeLabel(totals.impressionsChange)}
          changeDirection={getChangeDirection(totals.impressionsChange)}
          sparklineData={sparklineByMetric.impressions}
        />
        <BetaMetricCard
          label="Clicks"
          variant="clicks"
          isLoading={isLoading}
          value={formatCompactNumber(totals.clicks)}
          changeLabel={formatPercentChangeLabel(totals.clicksChange)}
          changeDirection={getChangeDirection(totals.clicksChange)}
          sparklineData={sparklineByMetric.clicks}
        />
        <BetaMetricCard
          label="Conversions"
          variant="conversions"
          isLoading={isLoading}
          value={formatCompactNumber(totals.conversions)}
          changeLabel={formatPercentChangeLabel(totals.conversionsChange)}
          changeDirection={getChangeDirection(totals.conversionsChange)}
          sparklineData={sparklineByMetric.conversions}
        />
      </BetaMetricCardRow>

      <BetaSectionHeading>
        02 — Performance Over Time
      </BetaSectionHeading>
      <BetaTimeSeriesChart
        data={mergedTimeSeries}
        subtitle={`Daily totals across enabled platforms · ${subtitle}`}
        isLoading={isLoading}
      />

      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-[1.6fr_1fr]">
        <OverviewBetaImpressionsSpend
          data={impressionsSpend}
          isLoading={isLoading}
        />
        <OverviewBetaCumulativeSpend
          data={cumulativeSpend}
          isLoading={isLoading}
        />
      </div>

      <BetaSectionHeading>
        03 — Platform Breakdown
      </BetaSectionHeading>
      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-[1.6fr_1fr]">
        <OverviewBetaSpendSegments
          segments={platformShare}
          isLoading={isLoading}
        />
        <OverviewBetaPlatformShare
          data={platformShare}
          isLoading={isLoading}
        />
      </div>

      <BetaSectionHeading>
        04 — Platform Comparison
      </BetaSectionHeading>
      <OverviewBetaPlatformComparison
        data={platformComparison}
        isLoading={isLoading}
      />

      <BetaSectionHeading>
        05 — Conversion Funnel
      </BetaSectionHeading>
      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-[1fr_1.5fr]">
        <OverviewBetaFunnel
          steps={funnelSteps}
          isLoading={isLoading}
        />
        <OverviewBetaConversionsTrend
          data={dateLabelPoints}
          isLoading={isLoading}
        />
      </div>

      <OverviewBetaEfficiency
        metrics={efficiencyMetrics}
        isLoading={isLoading}
      />
    </div>
  );
};

export default OverviewBetaTab;
